import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@app-shared/services/config.service';
import { APIResponse } from '@app-shared/services/req-res.types';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(private http: HttpClient, private config: ConfigService) {
    this.headers = this.headers.set('Api-Key', this.config.developerAPIKey);
  }

  /**
   * get() - GET request
   * @param path string
   * @param queryParams optional
   * @param reqHeaders optional
   * @returns Observable
   */
  get(path: string, queryParams?: object, reqHeaders?: object) {
    const options = this.constructOptions(queryParams, reqHeaders);
    return this.http
      .get<APIResponse>(this.config.apiBaseURL + path, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * post() - POST request
   * @param path string
   * @param params object
   * @param reqHeaders optional
   * @returns Observable
   */
  post(path: string, params: object, reqHeaders?: object) {
    const options = this.constructOptions(undefined, reqHeaders);
    //@ts-ignore
    const baseurl = params?.candidate ? this.config.candidateApiBaseURL : this.config.apiBaseURL;
    return this.http
      .post<APIResponse>(baseurl + path, params, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * postBlob() - POST request that expects a Blob response
   * @param path string
   * @param params object
   * @param reqHeaders optional
   * @returns Observable
   */
  postBlob(path: string, params: object, reqHeaders?: object) {
    const options = this.constructOptions(undefined, reqHeaders);
    //@ts-ignore
    const baseurl = params?.candidate ? this.config.candidateApiBaseURL : this.config.apiBaseURL;
    return this.http
      .post(baseurl + path, params, {
        ...options,
        responseType: 'blob' as const,
      })
      .pipe(catchError(this.handleError));
  }

  /**
  * put() - PUT request
  * @param path string
  * @param params object
  * @param reqHeaders optional
  * @returns Observable
  */
  put(path: string, params?: object, reqHeaders?: object) {
    const options = this.constructOptions(undefined, reqHeaders);
    return this.http
      .put<APIResponse>(this.config.apiBaseURL + path, params, options)
      .pipe(catchError(this.handleError));
  }

  /**
  * patch() - PATCH request
  * @param path string
  * @param params object
  * @param reqHeaders optional
  * @returns Observable
  */
  patch(path: string, params?: object, reqHeaders?: object) {
    const options = this.constructOptions(undefined, reqHeaders);
    return this.http
      .patch<APIResponse>(this.config.apiBaseURL + path, params, options)
      .pipe(catchError(this.handleError));
  }

  delete(path:string,id:string,reqHeaders?:object){
    const options = this.constructOptions(undefined, reqHeaders);
    return this.http
      .delete<APIResponse>(`${this.config.apiBaseURL}${path}/${id}`, options)
      .pipe(catchError(this.handleError));
  }
  /**
   * getExternal() - GET request
   * @param path string
   * @param queryParams optional
   * @param reqHeaders optional
   * @returns Observable
   */
  getExternal(path: string, queryParams?: object, reqHeaders?: object) {
    return this.http.get(path, {}).pipe(catchError(this.handleError));
  }

  /**
   * handleError
   * @param error HttpErrorResponse
   * @returns error handle / error message
   */
  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      return throwError(
        'Service backend might not be reachable, please contact administrator'
      );
    }
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong.
    return error?.error ? throwError(error.error): throwError(error);
  }

  private constructOptions(queryParams?: object, reqHeaders?: object): Object {
    let params = new HttpParams();
    let responseType: 'json' | 'blob' = 'json'; // Default response type
  
    // Set Query Params
    if (queryParams && Object.keys(queryParams).length > 0) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
  
      // Check if queryParams has {responseType: 'blob'}
      if (queryParams['responseType'] === 'blob') {
        responseType = 'blob';
      }
    }
  
    // Set Custom Headers
    if (reqHeaders && Object.keys(reqHeaders).length > 0) {
      Object.keys(reqHeaders).forEach((headerKey) => {
        this.headers = this.headers.set(headerKey, reqHeaders[headerKey]);
      });
    }
  
    // HTTP options
    return {
      headers: this.headers,
      observe: 'response' as const,
      responseType: responseType,
      params: params,
    };
  }
  
}
